import Modal from '../common/Modal'
import { chipClass, cn, helperTextClass, surfaceClass } from '../../utils/theme'
import { useProduct } from '../../hooks/useProducts'
import { formatCurrency } from '../../utils/currency'
import { getImageUrl } from '../../utils/url'

interface ProductViewModalProps {
  isOpen: boolean
  productId: string | number | null
  onClose: () => void
}

export function ProductViewModal({ productId, isOpen, onClose }: ProductViewModalProps) {
  const { data: product, isLoading } = useProduct(isOpen ? productId ?? undefined : undefined)

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Detalle del producto" size="md">
      {isLoading ? (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">Cargando…</p>
      ) : product ? (
        <div className="space-y-4">
          {product.imageUrl ? (
            <img
              src={getImageUrl(product.imageUrl)}
              alt={`Imagen de ${product.name}`}
              className="h-48 w-full rounded-lg object-cover"
            />
          ) : null}
          <div className="space-y-1">
            <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100">
              {product.name}
            </h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">{product.description}</p>
          </div>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            <div>
              <dt className={helperTextClass()}>Precio</dt>
              <dd className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                {formatCurrency(product.price)}
              </dd>
            </div>
            <div>
              <dt className={helperTextClass()}>Categoría</dt>
              <dd className="text-sm text-neutral-900 dark:text-neutral-100">
                {product.category?.name ?? 'Sin categoría'}
              </dd>
            </div>
            <div>
              <dt className={helperTextClass()}>Estado</dt>
              <dd>
                <span className={chipClass(product.active)}>
                  {product.active ? 'Activo' : 'Inactivo'}
                </span>
              </dd>
            </div>
            <div>
              <dt className={helperTextClass()}>Actualizado</dt>
              <dd className="text-sm text-neutral-900 dark:text-neutral-100">
                {product.updatedAt ? new Date(product.updatedAt).toLocaleString() : '—'}
              </dd>
            </div>
            <div>
              <dt className={helperTextClass()}>Máximo de sabores</dt>
              <dd className="text-sm text-neutral-900 dark:text-neutral-100">
                {typeof product.maxFlavors === 'number' ? product.maxFlavors : '—'}
              </dd>
            </div>
            <div>
              <dt className={helperTextClass()}>Cocina</dt>
              <dd className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                Cocina {product.preparationArea ?? 1}
              </dd>
            </div>
          </dl>
          {product.ingredients.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                Ingredientes
              </h4>
              <ul className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {product.ingredients.map((ingredient) => (
                  <li
                    key={ingredient.id}
                    className={surfaceClass('card')}
                  >
                    <span className="block rounded-md px-3 py-2 text-sm text-neutral-600 dark:text-neutral-200">
                      {ingredient.name}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {product.additions?.enabled && product.additions.options.length > 0 ? (
            <div>
              <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                Adiciones
              </h4>
              <ul className="mt-2 space-y-2">
                {product.additions.options.map((option) => (
                  <li key={option.key} className={cn(surfaceClass(), 'overflow-hidden rounded-lg')}>
                    <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                      <span className="font-medium text-neutral-900 dark:text-neutral-100">{option.name}</span>
                      <span className="text-neutral-600 dark:text-neutral-300">
                        {formatCurrency(option.priceDelta)}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 px-3 pb-3 text-xs text-neutral-500 dark:text-neutral-400">
                      <span>Máx. {option.maxQty}</span>
                      <span>{option.active ? 'Activa' : 'Inactiva'}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {product.exceptions?.enabled && product.exceptions.options.length > 0 ? (
            <div>
              <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                Excepciones
              </h4>
              <ul className="mt-2 space-y-2">
                {product.exceptions.options.map((option) => (
                  <li key={option.key} className={cn(surfaceClass(), 'overflow-hidden rounded-lg')}>
                    <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                      <span className="font-medium text-neutral-900 dark:text-neutral-100">{option.name}</span>
                      <span className="text-neutral-600 dark:text-neutral-300">
                        {option.affectsPrice
                          ? formatCurrency(option.priceDelta ?? 0)
                          : 'Sin impacto'}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 px-3 pb-3 text-xs text-neutral-500 dark:text-neutral-400">
                      <span>{option.affectsPrice ? 'Modifica precio' : 'Solo informativa'}</span>
                      <span>{option.active ? 'Activa' : 'Inactiva'}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">No encontramos el producto.</p>
      )}
    </Modal>
  )
}

export default ProductViewModal
